import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Card, CardBody } from '@/shared/components/Card';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { Button } from '@/shared/components/Button';
import { LoadingSpinner } from '@/shared/components/Loading';
import { useSettings, useUpdateSettings } from '@/services/settings';
import { ShippingCarrier } from '@/models/setting';
import { errorBannerStyle, successBannerStyle } from '@/shared/dark-inline-styles';

const Settings = () => {
    const { data, isLoading } = useSettings();
    const { mutateAsync: save, isPending: isSaving } = useUpdateSettings();

    const [taxEnabled, setTaxEnabled] = useState(false);
    const [shippingRateId, setShippingRateId] = useState('');
    // Meditation-event manual-payment bank destination (shown to registrants before receipt upload).
    const [eventCard, setEventCard] = useState('');
    const [eventIban, setEventIban] = useState('');
    const [eventAccountHolder, setEventAccountHolder] = useState('');
    const [eventInstructions, setEventInstructions] = useState('');
    const [carriers, setCarriers] = useState<ShippingCarrier[]>([]);
    const [dripEnabled, setDripEnabled] = useState(false);
    const [memberLiveSessionsEnabled, setMemberLiveSessionsEnabled] = useState(false);
    // App update gate. Kept as strings so the inputs can be blank (= no gate)
    // rather than coercing an empty field to 0, which would gate every build.
    const [iosMinBuild, setIosMinBuild] = useState('');
    const [androidMinBuild, setAndroidMinBuild] = useState('');
    const [iosLatestBuild, setIosLatestBuild] = useState('');
    const [androidLatestBuild, setAndroidLatestBuild] = useState('');
    const [iosStoreUrl, setIosStoreUrl] = useState('');
    const [androidStoreUrl, setAndroidStoreUrl] = useState('');
    const [appUpdateMessage, setAppUpdateMessage] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const s = data?.data;
        if (s) {
            setTaxEnabled(!!s.accessoriesTaxEnabled);
            setShippingRateId(s.accessoriesShippingRateId ?? '');
            setEventCard(s.eventPaymentCardNumber ?? '');
            setEventIban(s.eventPaymentIban ?? '');
            setEventAccountHolder(s.eventPaymentAccountHolder ?? '');
            setEventInstructions(s.eventPaymentInstructions ?? '');
            setCarriers(s.shippingCarriers ?? []);
            setDripEnabled(!!s.onboardingDripEnabled);
            setMemberLiveSessionsEnabled(!!s.memberLiveSessionsEnabled);
            const num = (v: number | null | undefined) => (typeof v === 'number' ? String(v) : '');
            setIosMinBuild(num(s.iosForceUpdateVersion));
            setAndroidMinBuild(num(s.androidForceUpdateVersion));
            setIosLatestBuild(num(s.iosLatestBuild));
            setAndroidLatestBuild(num(s.androidLatestBuild));
            setIosStoreUrl(s.iosStoreUrl ?? '');
            setAndroidStoreUrl(s.androidStoreUrl ?? '');
            setAppUpdateMessage(s.appUpdateMessage ?? '');
        }
    }, [data]);

    const setCarrier = (index: number, next: ShippingCarrier) => setCarriers((list) => list.map((c, i) => (i === index ? next : c)));
    const addCarrier = () => setCarriers((list) => [...list, { name: '', trackingUrlTemplate: '' }]);
    const removeCarrier = (index: number) => setCarriers((list) => list.filter((_, i) => i !== index));

    const handleSave = async () => {
        setError(null);
        setSaved(false);
        // Shipping rate is optional (blank = free shipping), but if set it must be a
        // Stripe Shipping Rate id — a malformed value breaks accessory checkout.
        const trimmedShippingRateId = shippingRateId.trim();
        if (trimmedShippingRateId && !/^shr_[A-Za-z0-9]+$/.test(trimmedShippingRateId)) {
            setError('Shipping Rate id must be a Stripe id like "shr_1AbC…", or blank for free shipping.');
            return;
        }
        // A tracking template WITHOUT the placeholder produces the same URL for
        // every parcel, so the API discards it silently. Catch it here instead,
        // where the person who typed it can still see why.
        const named = carriers.filter((c) => c.name.trim());
        const badTemplate = named.find((c) => c.trackingUrlTemplate?.trim() && !c.trackingUrlTemplate.includes('{trackingNumber}'));
        if (badTemplate) {
            setError(`The tracking URL for "${badTemplate.name}" must contain {trackingNumber}, or the same link is sent for every parcel.`);
            return;
        }

        // Build numbers: blank means "no gate". Anything else must be a positive
        // integer — a semver string here would be silently useless, because the
        // server compares build numbers.
        const builds: Array<[string, string]> = [
            ['iOS minimum supported build', iosMinBuild],
            ['Android minimum supported build', androidMinBuild],
            ['iOS latest build', iosLatestBuild],
            ['Android latest build', androidLatestBuild],
        ];
        const badBuild = builds.find(([, v]) => v.trim() && !/^[1-9][0-9]*$/.test(v.trim()));
        if (badBuild) {
            setError(`${badBuild[0]} must be a whole build number like 416 (not a version like 1.0.2), or blank for no gate.`);
            return;
        }
        // The dangerous mistake this catches: forcing an update with no store link,
        // which strands every user on a blocking screen with no way out.
        if (iosMinBuild.trim() && !iosStoreUrl.trim()) {
            setError('An iOS minimum build with no App Store URL would strand users on a blocking screen with no way to update.');
            return;
        }
        if (androidMinBuild.trim() && !androidStoreUrl.trim()) {
            setError('An Android minimum build with no Play Store URL would strand users on a blocking screen with no way to update.');
            return;
        }
        const toBuild = (v: string) => (v.trim() ? Number(v.trim()) : null);

        // callApi never throws on HTTP errors — inspect result.status.
        const res = await save({
            // Sent WHOLESALE — the API replaces the array rather than merging,
            // so a removed row must actually be absent here.
            shippingCarriers: named.map((c) => ({ name: c.name.trim(), trackingUrlTemplate: c.trackingUrlTemplate?.trim() || undefined })),
            onboardingDripEnabled: dripEnabled,
            memberLiveSessionsEnabled,
            accessoriesTaxEnabled: taxEnabled,
            accessoriesShippingRateId: trimmedShippingRateId,
            eventPaymentCardNumber: eventCard.trim(),
            eventPaymentIban: eventIban.trim(),
            eventPaymentAccountHolder: eventAccountHolder.trim(),
            eventPaymentInstructions: eventInstructions.trim(),
            iosForceUpdateVersion: toBuild(iosMinBuild),
            androidForceUpdateVersion: toBuild(androidMinBuild),
            iosLatestBuild: toBuild(iosLatestBuild),
            androidLatestBuild: toBuild(androidLatestBuild),
            iosStoreUrl: iosStoreUrl.trim(),
            androidStoreUrl: androidStoreUrl.trim(),
            appUpdateMessage: appUpdateMessage.trim(),
        });
        if (res.status === 'error') {
            setError(res.error?.message ?? 'Failed to save settings.');
            return;
        }
        setSaved(true);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Settings</h3>
                    <h3 className="page-subhead subhead">Runtime configuration — applied immediately, no deploy needed.</h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={8}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <MaterialFormContainer onSubmit={handleSave}>
                                    {error && <div style={errorBannerStyle}>{error}</div>}
                                    {saved && <div style={successBannerStyle}>Settings saved.</div>}

                                    <h4 style={{ fontWeight: 700, margin: '4px 0 12px' }}>Accessories</h4>

                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Stripe Tax on accessories checkout"
                                        value={taxEnabled ? 'on' : 'off'}
                                        onChange={(e) => setTaxEnabled(e.target.value === 'on')}
                                    >
                                        <MaterialFormOption value="off">Off (no tax)</MaterialFormOption>
                                        <MaterialFormOption value="on">On (Stripe Tax)</MaterialFormOption>
                                    </MaterialTextField>
                                    <p style={{ margin: '-6px 0 14px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>
                                        Only turn this on after finance has registered your tax jurisdictions in Stripe — otherwise Stripe Tax errors
                                        at checkout.
                                    </p>

                                    <MaterialTextField
                                        label="Flat shipping — Stripe Shipping Rate id (shr_…)"
                                        value={shippingRateId}
                                        onChange={(e) => setShippingRateId(e.target.value)}
                                    />
                                    <p style={{ margin: '-6px 0 14px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>
                                        Create a Shipping Rate in the Stripe dashboard and paste its id here to charge flat shipping. Leave blank for
                                        free shipping.
                                    </p>

                                    <h4 style={{ fontWeight: 700, margin: '24px 0 4px' }}>Shipping carriers</h4>
                                    <p style={{ margin: '0 0 12px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>
                                        The carriers ops can pick from when marking an order shipped. Which carrier serves where is an operations
                                        fact, so it lives here rather than in code. Leave empty and the order form falls back to a free-text carrier
                                        field. The tracking URL must contain <code>{'{trackingNumber}'}</code>; it is used to build the link in the
                                        customer's shipping email, and an unresolvable carrier sends NO link rather than a broken one.
                                    </p>
                                    {carriers.map((c, i) => (
                                        <div key={`carrier-${i}`} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                            <MaterialTextField
                                                label="Carrier name"
                                                value={c.name}
                                                onChange={(e) => setCarrier(i, { ...c, name: e.target.value })}
                                            />
                                            <MaterialTextField
                                                label="Tracking URL template"
                                                value={c.trackingUrlTemplate ?? ''}
                                                onChange={(e) => setCarrier(i, { ...c, trackingUrlTemplate: e.target.value })}
                                            />
                                            <Button variant="danger" size="sm" onClick={() => removeCarrier(i)} style={{ marginTop: 18 }}>
                                                Remove
                                            </Button>
                                        </div>
                                    ))}
                                    <Button variant="secondary" size="sm" onClick={addCarrier} style={{ marginBottom: 14 }}>
                                        Add carrier
                                    </Button>

                                    <h4 style={{ fontWeight: 700, margin: '24px 0 4px' }}>Onboarding emails</h4>
                                    <p style={{ margin: '0 0 12px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>
                                        The in-house onboarding drip. It ships OFF: customers are enrolled from day one so nobody is missing when it
                                        starts, but nothing sends until this is switched on. Turning it off again stops running sequences at the next
                                        daily run, with no deploy.
                                    </p>
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Onboarding drip"
                                        value={dripEnabled ? 'true' : 'false'}
                                        onChange={(e) => setDripEnabled(e.target.value === 'true')}
                                    >
                                        <MaterialFormOption value="false">Off — enrol only, send nothing</MaterialFormOption>
                                        <MaterialFormOption value="true">On — send the sequence</MaterialFormOption>
                                    </MaterialTextField>

                                    <h4 style={{ fontWeight: 700, margin: '24px 0 4px' }}>Member-created live sessions</h4>
                                    <p style={{ margin: '0 0 12px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>
                                        Whether MEMBERS may open their own live audio/video rooms and invite their connections. Coach-led sessions
                                        that staff create are unaffected by this and always work.
                                        <br />
                                        <br />
                                        Off is the safe setting and the default. A member-created room is live audio and video running on our
                                        Cloudflare bandwidth, between people we did not put in the room — so switching this on means accepting both
                                        the bandwidth cost and hosting rooms nobody on the team is in. Members who can create are held to one open
                                        session at a time and 12 people per room.
                                    </p>
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Member-created live sessions"
                                        value={memberLiveSessionsEnabled ? 'true' : 'false'}
                                        onChange={(e) => setMemberLiveSessionsEnabled(e.target.value === 'true')}
                                    >
                                        <MaterialFormOption value="false">Off — only staff can create live sessions</MaterialFormOption>
                                        <MaterialFormOption value="true">On — members can create their own</MaterialFormOption>
                                    </MaterialTextField>

                                    <h4 style={{ fontWeight: 700, margin: '24px 0 4px' }}>App update gate</h4>
                                    <p style={{ margin: '0 0 12px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>
                                        These are <strong>build numbers</strong> (the number that increments on every store upload), not version
                                        strings like 1.0.2. Leave a field blank to disable that gate. Nothing here is retroactive — it only affects
                                        apps the next time they check, which is at launch and on resume.
                                    </p>

                                    <MaterialTextField
                                        label="iOS — minimum supported build (blocks anything lower)"
                                        value={iosMinBuild}
                                        onChange={(e) => setIosMinBuild(e.target.value)}
                                    />
                                    <MaterialTextField
                                        label="iOS — latest build (offers a dismissible update)"
                                        value={iosLatestBuild}
                                        onChange={(e) => setIosLatestBuild(e.target.value)}
                                    />
                                    <MaterialTextField
                                        label="iOS — App Store URL"
                                        value={iosStoreUrl}
                                        onChange={(e) => setIosStoreUrl(e.target.value)}
                                    />

                                    <MaterialTextField
                                        label="Android — minimum supported build (blocks anything lower)"
                                        value={androidMinBuild}
                                        onChange={(e) => setAndroidMinBuild(e.target.value)}
                                    />
                                    <MaterialTextField
                                        label="Android — latest build (offers a dismissible update)"
                                        value={androidLatestBuild}
                                        onChange={(e) => setAndroidLatestBuild(e.target.value)}
                                    />
                                    <MaterialTextField
                                        label="Android — Play Store URL"
                                        value={androidStoreUrl}
                                        onChange={(e) => setAndroidStoreUrl(e.target.value)}
                                    />

                                    <MaterialTextField
                                        label="Message shown on the update screen (optional)"
                                        value={appUpdateMessage}
                                        onChange={(e) => setAppUpdateMessage(e.target.value)}
                                    />
                                    <p style={{ margin: '-6px 0 14px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>
                                        ⚠️ A <strong>minimum build</strong> is a hard block — people below it cannot use the app at all until they
                                        update. Set it only for a genuinely broken release, and make sure the build you are forcing people onto is
                                        actually live in the store first. The "latest build" field is the gentle one.
                                    </p>

                                    <h4 style={{ fontWeight: 700, margin: '24px 0 4px' }}>Event payments</h4>
                                    <p style={{ margin: '0 0 12px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>
                                        The bank account shown to people registering for a paid meditation event, so they can transfer the fee and
                                        upload a receipt. Same account for every event. Leave blank to hide the bank block.
                                    </p>
                                    <MaterialTextField label="Card number" value={eventCard} onChange={(e) => setEventCard(e.target.value)} />
                                    <MaterialTextField label="IBAN / Sheba" value={eventIban} onChange={(e) => setEventIban(e.target.value)} />
                                    <MaterialTextField
                                        label="Account holder name"
                                        value={eventAccountHolder}
                                        onChange={(e) => setEventAccountHolder(e.target.value)}
                                    />
                                    <MaterialTextField
                                        label="Payment instructions (optional)"
                                        multiline
                                        value={eventInstructions}
                                        onChange={(e) => setEventInstructions(e.target.value)}
                                    />
                                    <p style={{ margin: '-6px 0 14px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>
                                        Optional note shown under the account details (e.g. “transfer within 24 hours and upload your receipt”).
                                    </p>

                                    <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                                        {isSaving ? 'Saving…' : 'Save'}
                                    </Button>
                                </MaterialFormContainer>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Settings;
